import memoize from "lodash/memoize";
import { INTERNAL_DOMAINS } from "@app/util/AppConfig";
const asDict = (user) => {
    var _a;
    return {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone: user.phone,
        roles: (_a = user.roles) !== null && _a !== void 0 ? _a : [],
        profile: user.profile,
        /**
         * Used for validation
         * We remove `formula` and `parsed_formula` attributes to avoid
         * messing stuff up when parsing formulas that reference other formulas
         * */
        user: (() => {
            var _a, _b;
            const profile = Object.assign({}, user.profile);
            for (const key of Object.keys((_a = profile.fields) !== null && _a !== void 0 ? _a : {})) {
                if (((_b = profile.fields) === null || _b === void 0 ? void 0 : _b[key]) != null) {
                    if (profile.fields[key].formula != null) {
                        delete profile.fields[key].formula;
                    }
                    if (profile.fields[key].parsed_formula != null) {
                        delete profile.fields[key].parsed_formula;
                    }
                }
            }
            return profile;
        })(),
    };
};
const asDictMemoized = memoize(asDict);
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
class User {
    constructor(user) {
        var _a, _b, _c, _d, _e;
        Object.defineProperty(this, "hasRole", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (role) => {
                const _roles = Array.isArray(role) ? role : [role];
                return (_roles.filter((_role) => { var _a; return !!((_a = this.roles) === null || _a === void 0 ? void 0 : _a.map((r) => r.toLowerCase().trim()).includes(_role.toLowerCase().trim())); })
                    .length > 0);
            }
        });
        this.uid = user.uid;
        this.first_name = user.first_name;
        this.last_name = user.last_name;
        this.email = user.email;
        this.phone = user.phone;
        this.roles = user.roles;
        this.icon_url = user.icon_url;
        this.office = user.office;
        this.stage = (_c = (_a = user.stage) !== null && _a !== void 0 ? _a : (_b = user.profile) === null || _b === void 0 ? void 0 : _b.stage) !== null && _c !== void 0 ? _c : (_d = user.user) === null || _d === void 0 ? void 0 : _d.stage;
        this.profile = (_e = user.profile) !== null && _e !== void 0 ? _e : user.user;
        this.can_use_admin_only_comments = user.can_use_admin_only_comments;
    }
    /**
     * `true` if the user is an eligible leadership role who may see and post admin-only
     * (private) comments. Mirrors the backend `can_use_admin_only_comments` check (which also
     * accounts for Operating/Success Partner team roles not present in the flat `roles` array).
     */
    get canUseAdminOnlyComments() {
        return !!this.can_use_admin_only_comments;
    }
    /**
     * `true` if the user is signed in with a DASH account
     */
    get isInternal() {
        return INTERNAL_DOMAINS.some((domain) => { var _a; return (_a = this.email) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(`@${domain.toLowerCase().trim()}`); });
    }
    /**
     * `true` if the user is a developer
     * Developers sometimes have access to hidden / "beta" features
     */
    get isDeveloper() {
        return ((this.email &&
            ["developer@dashnc.com", "scripts@dashnc.com", "josh@dashnc.com", "alex.chibbra@dashnc.com"].includes(
            // TODO: this shouldn't be hard-coded... we should create a `developer` role.
            this.email.toLowerCase().trim())) ||
            false);
    }
    get name() {
        return [this.first_name, this.last_name].filter((obj) => obj).join(" ");
    }
    get initials() {
        return [this.first_name, this.last_name]
            .filter((obj) => obj)
            .map((obj) => (obj ? obj.substring(0, 1) : ""))
            .join("");
    }
    get asDict() {
        return asDictMemoized(this);
    }
}
export default User;
